import MapMarkerSvg from "@/src/assets/icons/icon-map-marker.svg";
import { useGetAddressByGeocdoeQuery } from "@/src/store/apis/common";
import { MediaCoordinateRangeType } from "@/src/store/apis/tasks/taskRegister/interface";
import { isUndefined } from "@/src/utils/is";
import {
  GoogleMap as GoogleMapComponent,
  GoogleMapProps,
  MarkerF,
  MarkerProps,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import Typo from "../Typo";

// Zoom level constants
const DEFAULT_MAX_ZOOM = 20;
const DEFAULT_MIN_ZOOM = 1;
const DEFAULT_ZOOM = 13;

interface Props extends GoogleMapProps {
  marker?: MarkerProps[];
  zoomLevel?: number;
  mediaCoordinateRange?: MediaCoordinateRangeType;
}

const GoogleMap = ({
  marker = [],
  mediaCoordinateRange,
  zoomLevel,
  ...mapProps
}: Props) => {
  const { i18n } = useTranslation();
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY as string,
    language: "-",
    region: "-",
  });

  const { geoInfo } = useGetAddressByGeocdoeQuery(
    {
      latitude: String(mapProps.center?.lat),
      longitude: String(mapProps.center?.lng),
      language: i18n.language as "en" | "ko",
    },
    {
      skip: !mapProps.center?.lat || !mapProps.center?.lng,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ isError, isFetching, currentData }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);

        return {
          geoInfo: isUnstable ? undefined : currentData.row.formattedAddress,
        };
      },
    },
  );

  const getZoomOptions = useCallback(() => {
    const validZoomLevel = zoomLevel > 0 ? zoomLevel : undefined;

    if (validZoomLevel) {
      // Ensure minZoom is never greater than maxZoom
      const safeZoomLevel = Math.max(
        DEFAULT_MIN_ZOOM,
        Math.min(DEFAULT_MAX_ZOOM, validZoomLevel),
      );
      return {
        maxZoom: DEFAULT_MAX_ZOOM,
        minZoom: DEFAULT_MIN_ZOOM,
        zoom: safeZoomLevel,
      };
    }

    return {
      maxZoom: DEFAULT_MAX_ZOOM,
      minZoom: DEFAULT_MIN_ZOOM,
      zoom: DEFAULT_ZOOM,
    };
  }, [zoomLevel]);

  const renderGeoInfo = () => {
    if (mediaCoordinateRange === "COUNTRY") {
      return i18n.language === "ko"
        ? geoInfo?.split(" ")[0]
        : geoInfo?.split(",")[1];
    }

    return geoInfo;
  };

  return (
    <GoogleMapWrapper>
      {isLoaded && (
        <GoogleMapComponent
          clickableIcons={false}
          options={{
            mapTypeControl: false,
            fullscreenControl: false,
            disableDoubleClickZoom: false,
            streetViewControl: false,
            draggable: false,
            keyboardShortcuts: false,
            scrollwheel: false,
            zoomControl: false,
            rotateControl: false,
            panControl: false,
            scaleControl: false,
            ...getZoomOptions(),
            styles: [
              {
                stylers: [{ visibility: "off" }],
                featureType: "poi",
                elementType: "labels",
              },
            ],
          }}
          {...mapProps}
        >
          {mediaCoordinateRange === "ALL" &&
            marker.map((markerProps, index) => (
              <MarkerF
                key={
                  markerProps.position
                    ? `marker-${markerProps.position.lat}-${markerProps.position.lng}`
                    : `marker-${index}`
                }
                icon={{
                  url: MapMarkerSvg,
                }}
                animation={google.maps.Animation.DROP}
                clickable={false}
                {...markerProps}
              />
            ))}
        </GoogleMapComponent>
      )}

      {geoInfo && (
        <LocationBox>
          <Typo typoType="b10r" color="white">
            Location {renderGeoInfo()}
          </Typo>
        </LocationBox>
      )}
    </GoogleMapWrapper>
  );
};

export default GoogleMap;

const GoogleMapWrapper = styled.div`
  position: relative;
`;

const LocationBox = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  padding: 4px 8px;
  bottom: 8px;
  left: 8px;
  right: 8px;
  border-radius: 2px;
`;
